import { useForm } from "react-hook-form";
import { useState, useEffect, useRef, Dispatch, SetStateAction } from "react";
import { getApi, useAuthContext } from '../../providers/AuthContext';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Save, CheckCircle2, Camera } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

import { PapInput, PapTextArea, PapImageUpload, PapErrors } from '../FormElements';

import { defaultImage } from '../../settings/config';

interface AccountProfileFormProps {
  profileState: [any, Dispatch<SetStateAction<any>>];
}

const schema = yup.object().shape({
  first_name: yup.string().optional().label('First Name'),
  last_name: yup.string().optional().label('Last Name'),
  home_city: yup.string().optional().label('Home City'),
  home_state: yup.string().optional().label('Home State'),
  phone_number: yup.string().optional().label('Cell Phone #'),
  bio: yup.string().optional().label('Bio'),
  image_id: yup.string().optional().nullable().label('Image'),
});

const AccountProfileForm: React.FC<AccountProfileFormProps> = ({ profileState }) => {
  const { loadUser } = useAuthContext();

  const [profile, setProfile] = profileState;
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState(defaultImage);
  const [profileSaved, setProfileSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  const resetErrors = () => {
    setPageErrors([]);
  };

  const resetPage = () => {
    reset();
    resetErrors();
    setImageUrl(defaultImage);
  };

  useEffect(() => {
    if (profile) {
      if (profile.image_url) {
        setImageUrl(profile.image_url);
      }
      reset(profile);
    }
  }, [profile]);

  const api = getApi();

  const saveProfile = async (data: Record<string, any>) => {
    setIsSubmitting(true);
    const profile_data = {
      profile: data
    };

    let result = null;

    try {
      result = await api.put('/api/v1/profiles/me', profile_data);
      setProfile(result.profile);
      loadUser();
      setPageErrors([]);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (ex: unknown) {
      setPageErrors([(ex as any)?.response?.error || 'An error occurred']);
    } finally {
      setIsSubmitting(false);
    }

    if (result) {
      resetPage();
    }
  };

  return (
    <div>
      <PapErrors errors={pageErrors} />

      <AnimatePresence>
        {profileSaved && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="flex items-center gap-2.5 p-3.5 bg-success/[0.06] dark:bg-success/[0.1] border border-success/20 rounded-xl mb-5 text-sm text-success"
          >
            <div className="w-7 h-7 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <span className="font-medium">Profile updated successfully.</span>
          </motion.div>
        )}
      </AnimatePresence>

      <form ref={formRef} onSubmit={handleSubmit(saveProfile)}>
        {/* Avatar upload - premium circular style */}
        <div className="flex justify-center mb-8">
          <div className="relative group">
            <div className="w-28 h-28 rounded-full ring-4 ring-border/30 dark:ring-border/20 ring-offset-4 ring-offset-card shadow-soft-md overflow-hidden">
              <PapImageUpload formBinding={register("image_id")} setFormValue={setValue as (name: string, value: any) => void} imageUrlState={[imageUrl, setImageUrl]} />
            </div>
            <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-soft-sm border-2 border-card transition-transform group-hover:scale-110">
              <Camera className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>

        {/* Name fields in grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <PapInput formBinding={register("first_name")} required={false} fieldLabel="First Name" fieldName="first_name" error={errors.first_name} />
          <PapInput formBinding={register("last_name")} required={false} fieldLabel="Last Name" fieldName="last_name" error={errors.last_name} />
        </div>

        {/* Location fields in grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <PapInput formBinding={register("home_city")} required={false} fieldLabel="Home City" fieldName="home_city" error={errors.home_city} />
          <PapInput formBinding={register("home_state")} required={false} fieldLabel="Home State" fieldName="home_state" error={errors.home_state} />
        </div>

        <PapInput formBinding={register("phone_number")} required={false} fieldLabel="Cell Phone #" type="tel" fieldName="phone_number" error={errors.phone_number} />
        <PapTextArea formBinding={register("bio")} fieldLabel="Bio" fieldName="bio" placeholder="Write something about yourself..." error={errors.bio} />

        <div className="mt-3 pt-3 border-t border-border/40">
          <button type="submit" className="btn btn-primary text-sm" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <Save className="w-4 h-4" />
                Save Profile
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountProfileForm;
